import {
  COMPANIES,
  SOCIAL_NETWORK_ORDER,
  SocialNetwork,
} from "./companyConfig";
import { imageAssetUrl, productionImageAssetUrl } from "./assets";
import { formatBrazilianPhone } from "./phoneFormat";
import { PhotoSignatureProps } from "./signatureTypes";

const SOCIAL_ICON_PATHS: Partial<Record<SocialNetwork, string>> = {
  instagram: "/social-icons/ico_instagram.png",
  linkedin: "/social-icons/ico_linkedin.png",
};
const SIGNATURE_BACKGROUND_COLOR = "#FFFFFF";
const LEGACY_BACKGROUND_COLOR_ATTRIBUTE = {
  bgcolor: SIGNATURE_BACKGROUND_COLOR,
} as Record<string, string>;
const SIGNATURE_BACKGROUND_STYLE = {
  backgroundColor: SIGNATURE_BACKGROUND_COLOR,
};

const normalizeExternalUrl = (url: string) => {
  if (/^[a-z]+:\/\//i.test(url) || url.startsWith("tel:")) {
    return url;
  }

  return `https://${url.replace(/^\/+/, "")}`;
};

const Signature = (props: PhotoSignatureProps) => {
  const company = COMPANIES[props.companyKey];
  const resolveImageUrl =
    props.assetMode === "production" ? productionImageAssetUrl : imageAssetUrl;
  const socialIcons = renderSocialIcons();
  const formattedPhone = formatBrazilianPhone(props.mobile);
  const phoneHref = formattedPhone
    ? `tel:+55${props.mobile.replace(/\D/g, "")}`
    : "";

  function renderSocialIcons() {
    const socialLinks = SOCIAL_NETWORK_ORDER
      .filter((network) => company.socialLinks[network])
      .map((network) => [
        network,
        normalizeExternalUrl(company.socialLinks[network] as string),
      ]) as [SocialNetwork, string][];

    return socialLinks.map(([network, href]) => (
        <a
          key={network}
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            marginRight: "6px",
            textDecoration: "none",
          }}
        >
          <img
            src={resolveImageUrl(
              SOCIAL_ICON_PATHS[network] ?? `/social-icons/${network}.svg`
            )}
            alt={network}
            width={18}
            height={18}
            style={{
              width: "18px",
              height: "18px",
              verticalAlign: "middle",
            }}
          />
        </a>
      ));
  }

  return (
    <div
      className="signature"
      style={{
        display: "inline-block",
        backgroundColor: SIGNATURE_BACKGROUND_COLOR,
      }}
    >
      <table
        {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
        cellPadding={0}
        cellSpacing={0}
        style={{
          width: "575px",
          whiteSpace: "normal",
          ...SIGNATURE_BACKGROUND_STYLE,
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        <tbody
          {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
          style={SIGNATURE_BACKGROUND_STYLE}
        >
          <tr
            {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
            style={SIGNATURE_BACKGROUND_STYLE}
          >
            <td
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={{
                ...SIGNATURE_BACKGROUND_STYLE,
                color: "#4D7CFE",
                fontFamily: "Helvetica, Arial, sans-serif",
                fontWeight: "bold",
                fontSize: "20px",
                lineHeight: "24px",
                paddingBottom: "2px",
              }}
            >
              {props.fullName}
              {props.pronoun ? (
                <span
                  style={{
                    color: "#6C6F73",
                    fontSize: "12px",
                    fontStyle: "italic",
                    fontWeight: "normal",
                  }}
                >
                  {" "}
                  ({props.pronoun})
                </span>
              ) : (
                ""
              )}
            </td>
          </tr>
          <tr
            {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
            style={SIGNATURE_BACKGROUND_STYLE}
          >
            <td
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={{
                ...SIGNATURE_BACKGROUND_STYLE,
                color: "#7F8489",
                fontFamily: "Helvetica, Arial, sans-serif",
                fontSize: "13px",
                lineHeight: "18px",
                textTransform: "uppercase",
                paddingBottom: "10px",
              }}
            >
              {props.title}
            </td>
          </tr>
          <tr
            {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
            style={SIGNATURE_BACKGROUND_STYLE}
          >
            <td
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={{
                ...SIGNATURE_BACKGROUND_STYLE,
                paddingBottom: "12px",
              }}
            >
              <img
                src={resolveImageUrl("/line-gradient.png")}
                alt=""
                aria-hidden="true"
                style={{
                  display: "block",
                  width: "100%",
                  height: "2px",
                }}
              />
            </td>
          </tr>
          <tr
            {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
            style={SIGNATURE_BACKGROUND_STYLE}
          >
            <td
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={{
                ...SIGNATURE_BACKGROUND_STYLE,
                paddingBottom: "10px",
              }}
            >
              <img
                width={company.logoWidth}
                height={company.logoHeight}
                style={{
                  display: "block",
                  width: `${company.logoWidth}px`,
                  height: `${company.logoHeight}px`,
                }}
                src={resolveImageUrl(company.logoPath)}
                alt="company-logo"
              />
            </td>
          </tr>
          <tr
            {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
            style={SIGNATURE_BACKGROUND_STYLE}
          >
            <td
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={{
                ...SIGNATURE_BACKGROUND_STYLE,
                color: "#6C6F73",
                fontFamily: "Helvetica, Arial, sans-serif",
                fontSize: "13px",
                lineHeight: "18px",
                paddingBottom: "2px",
              }}
            >
              <a
                href={normalizeExternalUrl(company.mapsUrl)}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "#6C6F73",
                  fontWeight: "normal",
                  textDecoration: "none",
                  textTransform: "none",
                }}
              >
                {company.address}
              </a>
            </td>
          </tr>
          {formattedPhone ? (
            <tr
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={SIGNATURE_BACKGROUND_STYLE}
            >
              <td
                {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
                style={{
                  ...SIGNATURE_BACKGROUND_STYLE,
                  color: "#6C6F73",
                  fontFamily: "Helvetica, Arial, sans-serif",
                  fontSize: "13px",
                  lineHeight: "18px",
                  paddingBottom: "2px",
                }}
              >
                <a
                  href={normalizeExternalUrl(phoneHref)}
                  style={{
                    color: "#6C6F73",
                    fontWeight: "normal",
                    textDecoration: "none",
                    textTransform: "none",
                  }}
                >
                  +55{formattedPhone}
                </a>
              </td>
            </tr>
          ) : null}
          <tr
            {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
            style={SIGNATURE_BACKGROUND_STYLE}
          >
            <td
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={{
                ...SIGNATURE_BACKGROUND_STYLE,
                paddingBottom: socialIcons.length > 0 ? "8px" : "0",
              }}
            >
              <a
                href={normalizeExternalUrl(company.websiteUrl)}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "#4D7CFE",
                  fontFamily: "Helvetica, Arial, sans-serif",
                  fontSize: "13px",
                  lineHeight: "18px",
                  textDecoration: "none",
                  textTransform: "none",
                }}
              >
                {company.websiteLabel}
              </a>
            </td>
          </tr>
          {socialIcons.length > 0 ? (
            <tr
              {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
              style={SIGNATURE_BACKGROUND_STYLE}
            >
              <td
                {...LEGACY_BACKGROUND_COLOR_ATTRIBUTE}
                style={SIGNATURE_BACKGROUND_STYLE}
              >
                {socialIcons}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

export default Signature;
